import axios from 'axios';
import { isElectron } from '../utils/electronBridge';

const CLIENT_PLATFORM = isElectron ? 'desktop' : 'web';

/**
 * Unified API Client
 * Centralizes all HTTP requests with consistent error handling, auth headers, and retry logic
 *
 * Benefits:
 * - Automatic auth token injection
 * - Consistent error handling (throws standardized errors)
 * - Token refresh on 401
 * - Request/response logging in development
 * - No manual try-catch wrapping needed
 */

// Create axios instance with defaults
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL || 'http://localhost:3002',
  timeout: 30000,
  withCredentials: true, // Send cookies with every request
  headers: {
    'Content-Type': 'application/json'
  }
});

// Create AI agent specific instance
const aiApiClient = axios.create({
  baseURL: process.env.REACT_APP_AGENT_API_URL || 'http://localhost:8001',
  timeout: 120000, // Longer timeout for AI streams
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - adds auth token automatically
const requestInterceptor = (config) => {
    // Cookies are sent automatically with withCredentials: true
    // No need to manually add Authorization header - httpOnly cookies handle auth

    config.headers['X-Client-Platform'] = CLIENT_PLATFORM;

    // Fallback: check localStorage ONLY if cookies aren't being used (backwards compatibility)
    // Only add Authorization header if explicitly needed for non-cookie endpoints
    const token = localStorage.getItem('authToken');

    if (token && token !== 'undefined' && token !== 'null' && token.length > 20) {
      // Validate token format before adding Bearer prefix
      config.headers.Authorization = `Bearer ${token}`;
    }

    // When sending FormData, remove Content-Type so the browser sets
    // the correct multipart/form-data boundary automatically.
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Log requests in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] ${config.method.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
        withCredentials: config.withCredentials,
        hasAuthHeader: !!config.headers.Authorization
      });
    }

    return config;
};

apiClient.interceptors.request.use(requestInterceptor, error => Promise.reject(error));
aiApiClient.interceptors.request.use(requestInterceptor, error => Promise.reject(error));

// Response interceptor - handles errors consistently
const responseInterceptorSuccess = (response) => {
    // Log successful responses in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] ✓ ${response.config.method.toUpperCase()} ${response.config.url}`, response.data);
    }

    return response;
  };

// Debounce flag so concurrent 401s only trigger one logout
let _loggingOut = false;

function forceLogout(reason) {
  if (_loggingOut) return;
  _loggingOut = true;

  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('currentUser');
  // Same leak authService.js's clearStoredCredentials() fixes -- this is
  // the OTHER logout path (a 401 forcing logout here, not the user clicking
  // Logout), so it needs the identical fix independently, not just a shared
  // call, since the two paths don't otherwise share code.
  localStorage.removeItem('screenwriter_workspace_state_v3');

  // App.js listens for 'userLoggedOut' on document
  document.dispatchEvent(new CustomEvent('userLoggedOut', { detail: { reason } }));

  // Reset flag after a tick so any in-flight retries also trigger the event
  setTimeout(() => { _loggingOut = false; }, 3000);
}

const responseInterceptorError = async (error) => {
    const originalRequest = error.config;

    // Log errors in development
    if (process.env.NODE_ENV === 'development') {
      console.error(`[API] ✗ ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        status: error.response?.status,
        message: error.response?.data?.message || error.message
      });
    }

    // A file's own 403 for "you're not a collaborator on this" (server/
    // routes/files.js's GET /:fileId) is a real, offerable situation, not a
    // dead end -- dispatch a DOM event (App.js has toast/navigate in scope
    // to actually show something; this module doesn't) so it can offer
    // "Request Access" instead of the request just silently failing. Same
    // outside-React-reaching-into-React pattern this file already uses for
    // 'userLoggedOut' below.
    if (error.response?.status === 403 && error.response?.data?.code === 'FILE_ACCESS_DENIED') {
      document.dispatchEvent(new CustomEvent('fileAccessDenied', { detail: error.response.data }));
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      const hasAuthToken = localStorage.getItem('authToken');
      const hasRefreshToken = localStorage.getItem('refreshToken');
      const isLoggedIn = !!localStorage.getItem('currentUser');

      if (hasAuthToken && hasRefreshToken) {
        // Token-based auth: attempt refresh first
        originalRequest._retry = true;

        try {
          const response = await axios.post(
            `${apiClient.defaults.baseURL}/api/auth/refresh`,
            { refreshToken: hasRefreshToken }
          );

          const { accessToken, refreshToken: newRefreshToken } = response.data.tokens;

          localStorage.setItem('authToken', accessToken);
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          // Refresh failed — log out so the app doesn't keep retrying
          forceLogout('token_refresh_failed');
          return Promise.reject(refreshError);
        }
      }

      // Cookie-based auth (or no refresh token): session expired.
      // Log out immediately instead of letting the sync loop retry forever.
      if (isLoggedIn) {
        forceLogout('session_expired');
      }
    }

    const standardError = createStandardError(error);
    return Promise.reject(standardError);
  };

apiClient.interceptors.response.use(responseInterceptorSuccess, responseInterceptorError);
aiApiClient.interceptors.response.use(responseInterceptorSuccess, responseInterceptorError);

/**
 * Creates a standardized error object
 * @param {Error} error - Original error
 * @returns {Error} Standardized error with consistent properties
 */
function createStandardError(error) {
  const standardError = new Error();

  if (error.response) {
    // Server responded with error status
    standardError.message = error.response.data?.message || error.response.data?.error || error.response.data?.detail || error.response.statusText || 'Request failed';
    standardError.status = error.response.status;
    standardError.data = error.response.data;
    standardError.isApiError = true;
  } else if (error.request) {
    // Request made but no response
    standardError.message = 'Network error - please check your connection';
    standardError.status = 0;
    standardError.isNetworkError = true;
  } else {
    // Something else happened
    standardError.message = error.message || 'An unexpected error occurred';
    standardError.status = 0;
  }

  standardError.originalError = error;
  return standardError;
}

/**
 * Creates a wrapper around an axios instance with convenience methods.
 * @param {import('axios').AxiosInstance} client - Axios instance
 * @returns {object} API helper with get, post, put, patch, delete, instance
 */
function createApiHelper(client) {
  return {
    async get(url, config = {}) {
      const response = await client.get(url, config);
      return response.data;
    },
    async post(url, data, config = {}) {
      const response = await client.post(url, data, config);
      return response.data;
    },
    async put(url, data, config = {}) {
      const response = await client.put(url, data, config);
      return response.data;
    },
    async patch(url, data, config = {}) {
      const response = await client.patch(url, data, config);
      return response.data;
    },
    async delete(url, config = {}) {
      const response = await client.delete(url, config);
      return response.data;
    },
    instance: client
  };
}

export const api = createApiHelper(apiClient);
export const aiApi = createApiHelper(aiApiClient);

export default api;
